/**
 * Normalize any article payload into clean HTML for the article editor.
 *
 * Accepts HTML strings, JSON strings, TipTap docs, and article objects
 * (`{ body }`, `{ html }`, `{ content }`, …). Strips chat timestamps,
 * paste inline styles, and wrapping <span> noise from copied messages.
 *
 * When a TipTap doc stores HTML tags as literal text (so the editor would
 * show `<p>…</p>` instead of rendering it), those tags are extracted and
 * returned as real HTML for TipTap to parse.
 *
 * @param {unknown} raw
 * @returns {string}
 */
export function formatArticleHtml(raw) {
  const extracted = extractHtmlFromArticle(raw, 0, new WeakSet());
  const cleaned = cleanArticleHtml(extracted);
  return cleaned || "<p></p>";
}

/**
 * True when JSON body should be ignored so the editor loads HTML instead:
 * empty TipTap docs, or docs whose text nodes are raw/escaped HTML tags.
 *
 * @param {unknown} body
 * @returns {boolean}
 */
export function shouldPreferArticleHtml(body) {
  if (body == null || typeof body !== "object" || Array.isArray(body)) return true;
  if (articleDocContainsRawHtml(body)) return true;
  if (body.type === "doc" && !hasMeaningfulTipTapText(body)) return true;
  return false;
}

/**
 * @param {unknown} doc
 * @returns {boolean}
 */
export function articleDocContainsRawHtml(doc) {
  if (!doc || typeof doc !== "object") return false;
  let found = false;
  function walk(node) {
    if (found || !node || typeof node !== "object") return;
    if (typeof node.text === "string" && textLooksLikeHtml(node.text)) {
      found = true;
      return;
    }
    if (typeof node.content === "string" && textLooksLikeHtml(node.content)) {
      found = true;
      return;
    }
    if (Array.isArray(node.content)) node.content.forEach(walk);
  }
  walk(doc);
  return found;
}

/**
 * Parse a TipTap JSON article body from an object or JSON string.
 *
 * @param {unknown} raw
 * @returns {object|null}
 */
export function parseArticleBodyPayload(raw) {
  if (raw == null) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

function cloneArticleDoc(doc) {
  if (!doc || typeof doc !== "object") return null;
  try {
    return JSON.parse(JSON.stringify(doc));
  } catch {
    return null;
  }
}

/**
 * Resolve TipTap JSON vs HTML for `<exiq-notion-editor>` — same rules as CheckpointMicrolearningMenu.
 *
 * @param {{ articleHtml?: unknown, articleBody?: unknown }} payload
 * @returns {{ contentDoc: object|null, valueHtml: string }}
 */
export function resolveNotionArticleContent({ articleHtml, articleBody } = {}) {
  const body = parseArticleBodyPayload(articleBody);
  if (body && !shouldPreferArticleHtml(body)) {
    return { contentDoc: cloneArticleDoc(body), valueHtml: "" };
  }
  const fromHtmlProp = formatArticleHtml(articleHtml);
  const html =
    fromHtmlProp !== "<p></p>"
      ? fromHtmlProp
      : formatArticleHtml(body && articleDocContainsRawHtml(body) ? body : articleHtml);
  return { contentDoc: null, valueHtml: html };
}

/** True when HTML has no visible text after stripping tags and whitespace. */
export function isArticleHtmlVisuallyEmpty(html) {
  const s = String(html ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, "")
    .trim();
  return s.length === 0;
}

/** True when TipTap JSON has no non-whitespace text. */
export function isArticleDocVisuallyEmpty(doc) {
  return !hasMeaningfulTipTapText(doc);
}

/**
 * Wrap plain-text paragraphs as HTML for the Notion article viewer.
 *
 * @param {unknown} paragraphs
 * @returns {string}
 */
export function paragraphsToArticleHtml(paragraphs) {
  if (!Array.isArray(paragraphs) || !paragraphs.length) return "<p></p>";
  const parts = paragraphs
    .map((paragraph) => {
      const text = typeof paragraph === "string" ? paragraph : paragraph?.text || "";
      const html = formatArticleHtml(text);
      return html === "<p></p>" ? "" : html;
    })
    .filter(Boolean);
  return parts.join("") || "<p></p>";
}

const MAX_DEPTH = 8;

const CHAT_TIMESTAMP_RE = /\[\s*\d{1,2}:\d{2}(?::\d{2})?\s*(?:[AaPp]\.?\s*[Mm]\.?)\s*\]/g;

const HTML_TAG_RE = /<[a-z][\s\S]*>/i;
const ESCAPED_HTML_TAG_RE = /&lt;\/?[a-z]/i;

const TIPTAP_BLOCK_TYPES = new Set([
  "doc",
  "paragraph",
  "heading",
  "bulletList",
  "orderedList",
  "listItem",
  "blockquote",
  "codeBlock",
  "hardBreak",
  "horizontalRule",
  "text",
]);

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function textLooksLikeHtml(text) {
  const s = String(text ?? "");
  return HTML_TAG_RE.test(s) || ESCAPED_HTML_TAG_RE.test(s);
}

function hasMeaningfulTipTapText(node) {
  if (!node || typeof node !== "object") return false;
  if (node.type === "text" && String(node.text || "").trim() !== "") return true;
  if (Array.isArray(node.content)) return node.content.some(hasMeaningfulTipTapText);
  return false;
}

function decodeEscapedHtml(value) {
  const s = String(value ?? "");
  if (!ESCAPED_HTML_TAG_RE.test(s) || HTML_TAG_RE.test(s)) return s;
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");
}

function extractRawHtmlFromTipTap(doc) {
  const chunks = [];
  function walk(node) {
    if (!node || typeof node !== "object") return;
    if (typeof node.text === "string" && textLooksLikeHtml(node.text)) {
      chunks.push(decodeEscapedHtml(node.text));
      return;
    }
    if (typeof node.content === "string" && textLooksLikeHtml(node.content)) {
      chunks.push(decodeEscapedHtml(node.content));
      return;
    }
    if (Array.isArray(node.content)) node.content.forEach(walk);
  }
  walk(doc);
  return chunks.join("");
}

function extractHtmlFromArticle(raw, depth, seen) {
  if (raw == null || depth > MAX_DEPTH) return "";

  if (typeof raw === "string") {
    const trimmed = decodeEscapedHtml(raw.trim());
    if (!trimmed) return "";
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed !== raw) {
          const nested = extractHtmlFromArticle(parsed, depth + 1, seen);
          if (nested) return nested;
        }
      } catch {
        // Not JSON — treat as HTML / plain text below.
      }
    }
    return trimmed;
  }

  if (typeof raw === "number" || typeof raw === "boolean") {
    return String(raw);
  }

  if (Array.isArray(raw)) {
    return raw
      .map((item) => extractHtmlFromArticle(item, depth + 1, seen))
      .filter(Boolean)
      .join("");
  }

  if (!isPlainObject(raw) || seen.has(raw)) return "";
  seen.add(raw);

  if (isTipTapNode(raw)) {
    if (articleDocContainsRawHtml(raw)) {
      return extractRawHtmlFromTipTap(raw);
    }
    return serializeTipTap(raw);
  }

  const fieldOrder = [
    "html",
    "article_html",
    "knowledge_article_html",
    "proposed_knowledge_article_html",
    "body",
    "content",
    "rendered",
    "text",
    "article",
    "proposed_article",
    "active_article",
    "knowledge_article",
  ];

  for (const key of fieldOrder) {
    if (raw[key] == null || raw[key] === "") continue;
    const nested = extractHtmlFromArticle(raw[key], depth + 1, seen);
    if (nested) return nested;
  }

  return "";
}

function isTipTapNode(value) {
  if (!isPlainObject(value) || typeof value.type !== "string") return false;
  if (value.body != null || value.html != null) return false;
  if (value.type === "doc") return true;
  if (!TIPTAP_BLOCK_TYPES.has(value.type)) return false;
  return Array.isArray(value.content) || typeof value.text === "string";
}

function serializeTipTap(node) {
  if (!node || typeof node !== "object") return "";
  if (node.type === "text") {
    const text = node.text == null ? "" : String(node.text);
    if (!text) return "";
    const inner = HTML_TAG_RE.test(text) ? text : escapeHtml(text);
    return applyTipTapMarks(inner, node.marks);
  }
  if (node.type === "hardBreak") return "<br>";
  if (node.type === "horizontalRule") return "<hr>";

  const inner = Array.isArray(node.content) ? node.content.map(serializeTipTap).join("") : "";

  switch (node.type) {
    case "doc":
      return inner;
    case "paragraph":
      return `<p>${inner}</p>`;
    case "heading": {
      const level = Math.min(6, Math.max(1, Number(node.attrs?.level) || 1));
      return `<h${level}>${inner}</h${level}>`;
    }
    case "bulletList":
      return `<ul>${inner}</ul>`;
    case "orderedList":
      return `<ol>${inner}</ol>`;
    case "listItem":
      return `<li>${inner}</li>`;
    case "blockquote":
      return `<blockquote>${inner}</blockquote>`;
    case "codeBlock":
      return `<pre><code>${inner}</code></pre>`;
    default:
      return inner;
  }
}

function applyTipTapMarks(html, marks) {
  if (!Array.isArray(marks) || !marks.length) return html;
  return marks.reduce((acc, mark) => {
    const type = mark?.type;
    if (type === "bold" || type === "strong") return `<strong>${acc}</strong>`;
    if (type === "italic" || type === "em") return `<em>${acc}</em>`;
    if (type === "underline") return `<u>${acc}</u>`;
    if (type === "code") return `<code>${acc}</code>`;
    if (type === "link" && mark.attrs?.href) {
      return `<a href="${escapeHtml(String(mark.attrs.href))}">${acc}</a>`;
    }
    return acc;
  }, html);
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function cleanArticleHtml(html) {
  let out = String(html ?? "");
  if (!out.trim()) return "";

  out = decodeEscapedHtml(out);
  out = out.replace(/<script\b[\s\S]*?<\/script>/gi, "");
  out = out.replace(/<style\b[\s\S]*?<\/style>/gi, "");
  out = out.replace(/<!--[\s\S]*?-->/g, "");
  out = out.replace(CHAT_TIMESTAMP_RE, "");
  out = out.replace(/\s+style\s*=\s*(["'])[\s\S]*?\1/gi, "");
  out = out.replace(/\s+style\s*=\s*[^\s>]+/gi, "");

  let previous = "";
  let unwrapPasses = 0;
  while (previous !== out && unwrapPasses < 20) {
    previous = out;
    unwrapPasses += 1;
    out = out.replace(/<\/?(?:span|font)\b[^>]*>/gi, "");
  }

  out = out.replace(/<p(?:\s[^>]*)?>\s*<\/p>/gi, "");
  out = out.replace(/>\s+</g, "><");
  out = out.trim();

  if (!out) return "";
  if (!HTML_TAG_RE.test(out)) {
    return `<p>${out}</p>`;
  }
  return out;
}
